import { useState } from "react";
import {
  Stack,
  Paper,
  Text,
  Group,
  Badge,
  Button,
  Table,
  Modal,
  Textarea,
  Tooltip,
  ActionIcon,
  Loader,
  Center,
  Box,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCheck, IconX, IconClock, IconInbox } from "@tabler/icons-react";
import {
  usePendingMacroRequests,
  useApproveMacroRequest,
  useRejectMacroRequest,
} from "../../../hooks/admin/useMacroRequests";
import { MacroRequest } from "../../../services/admin/macroRequests.service";

export default function PendingMacroRequests() {
  const { data: requests = [], isLoading } = usePendingMacroRequests();
  const approveMutation = useApproveMacroRequest();
  const rejectMutation = useRejectMacroRequest();

  const [
    reviewModalOpened,
    { open: openReviewModal, close: closeReviewModal },
  ] = useDisclosure(false);
  const [selectedRequest, setSelectedRequest] = useState<MacroRequest | null>(
    null
  );
  const [reviewAction, setReviewAction] = useState<"approve" | "reject">(
    "approve"
  );
  const [reviewNotes, setReviewNotes] = useState("");

  const handleReview = (
    request: MacroRequest,
    action: "approve" | "reject"
  ) => {
    setSelectedRequest(request);
    setReviewAction(action);
    setReviewNotes("");
    openReviewModal();
  };

  const handleConfirmReview = async () => {
    if (!selectedRequest) return;

    if (reviewAction === "approve") {
      await approveMutation.mutateAsync({
        id: selectedRequest.id,
        notes: reviewNotes || undefined,
      });
    } else {
      if (!reviewNotes.trim()) return;
      await rejectMutation.mutateAsync({
        id: selectedRequest.id,
        notes: reviewNotes,
      });
    }

    closeReviewModal();
    setSelectedRequest(null);
  };

  if (isLoading) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }

  if (requests.length === 0) {
    return (
      <Paper p="xl" withBorder>
        <Center>
          <Stack align="center" gap="xs">
            <IconInbox size={40} color="var(--mantine-color-gray-5)" />
            <Text c="dimmed">No pending macro requests</Text>
          </Stack>
        </Center>
      </Paper>
    );
  }

  return (
    <>
      <Paper p="md" withBorder>
        <Stack gap="md">
          <Group justify="space-between">
            <Group gap="sm">
              <IconClock size={20} color="var(--mantine-color-orange-6)" />
              <Text fw={600}>Pending Macro Requests</Text>
              <Badge color="orange" variant="filled" size="sm">
                {requests.length}
              </Badge>
            </Group>
          </Group>

          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 130 }}>Code</Table.Th>
                <Table.Th>Diagnosis Description</Table.Th>
                <Table.Th style={{ width: 130 }}>Category</Table.Th>
                <Table.Th style={{ width: 100 }}>CPT</Table.Th>
                <Table.Th style={{ width: 100 }}>ICD</Table.Th>
                <Table.Th style={{ width: 140 }}>Requested By</Table.Th>
                <Table.Th style={{ width: 130 }}>Date</Table.Th>
                <Table.Th style={{ width: 100 }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {requests.map((request) => (
                <Table.Tr key={request.id}>
                  <Table.Td>
                    <Text size="sm" fw={600} ff="monospace">
                      {request.code}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" lineClamp={2}>
                      {request.diagnosisDescription || "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {request.category?.name ? (
                      <Badge color="blue" variant="light" size="sm">
                        {request.category.name}
                      </Badge>
                    ) : (
                      <Text size="sm" c="dimmed">
                        —
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {request.cptCode ? (
                      <Tooltip label={request.cptCode.description}>
                        <Text size="sm">{request.cptCode.code}</Text>
                      </Tooltip>
                    ) : (
                      <Text size="sm" c="dimmed">
                        —
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {request.icdCode ? (
                      <Tooltip label={request.icdCode.description}>
                        <Text size="sm">{request.icdCode.code}</Text>
                      </Tooltip>
                    ) : (
                      <Text size="sm" c="dimmed">
                        —
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {request.requestedBy?.fullName || "Unknown"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {new Date(request.createdAt).toLocaleDateString()}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Tooltip label="Approve">
                        <ActionIcon
                          variant="subtle"
                          color="green"
                          onClick={() => handleReview(request, "approve")}
                        >
                          <IconCheck size={18} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Reject">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => handleReview(request, "reject")}
                        >
                          <IconX size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      </Paper>

      <Modal
        opened={reviewModalOpened}
        onClose={closeReviewModal}
        title={
          reviewAction === "approve"
            ? "Approve Macro Request"
            : "Reject Macro Request"
        }
        size="md"
        centered
      >
        {selectedRequest && (
          <Stack gap="md">
            <Box>
              <Text size="sm" fw={600}>
                Code:{" "}
                <Text span ff="monospace">
                  {selectedRequest.code}
                </Text>
              </Text>
              {selectedRequest.diagnosisDescription && (
                <Text size="sm" mt="xs">
                  <Text span fw={500}>
                    Description:
                  </Text>{" "}
                  {selectedRequest.diagnosisDescription}
                </Text>
              )}
              <Text size="sm" c="dimmed" mt="xs">
                Requested by{" "}
                {selectedRequest.requestedBy?.fullName || "Unknown"} on{" "}
                {new Date(selectedRequest.createdAt).toLocaleDateString()}
              </Text>
            </Box>

            {reviewAction === "approve" ? (
              <Text size="sm">
                This will create a new global diagnosis macro from this request.
              </Text>
            ) : (
              <Text size="sm" c="red">
                The requester will be notified that their request was rejected.
              </Text>
            )}

            <Textarea
              label={
                reviewAction === "approve"
                  ? "Review Notes (optional)"
                  : "Rejection Reason"
              }
              placeholder={
                reviewAction === "approve"
                  ? "Any notes about this approval..."
                  : "Explain why this request is being rejected..."
              }
              withAsterisk={reviewAction === "reject"}
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              minRows={3}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeReviewModal}>
                Cancel
              </Button>
              <Button
                color={reviewAction === "approve" ? "green" : "red"}
                loading={approveMutation.isPending || rejectMutation.isPending}
                onClick={handleConfirmReview}
                disabled={reviewAction === "reject" && !reviewNotes.trim()}
              >
                {reviewAction === "approve"
                  ? "Approve & Create Macro"
                  : "Reject"}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
